import { Cta, CtaBar, CtaNote, CtaSpinner, SubmitError } from '../styles';
import { BookingAddOnsStep } from '../use-booking-add-ons.hook';

import React from 'react';
import { Spinner } from '@hqo/react-components-library/dist/atoms/spinner';
import { formatCurrency } from 'utils/formatCurrency';
import { useIntl } from 'react-intl';
import { useLocale } from 'hooks/use-locale.hook';

interface StepCtaProps {
  hasSelection: boolean;
  /* Minor units. The client's own sum of catalog unit prices, not the charge: this sum never includes
   * tax for any party, and billing's sheet states the real total at the next step. */
  estimateTotal: number;
  /* The currency the total is in, or null when the selection spans more than one — or lacks one. */
  estimateCurrency: string | null;
  /*
   * RES-1336 adds the spinner this drives. The button is the last thing the tenant touched, so it is
   * where the write is reported; the wait that FOLLOWS it — billing opening a session — is reported
   * on the confirmation instead, because by then this button is gone.
   */
  isSubmitting: boolean;
  hasSubmitFailed: boolean;
  /*
   * ⭐ ONE handler, resolved by the hook. It was two — `onSkip` when nothing was selected and
   * `onSubmit` when something was — but RES-1057 adds a third destination (open the vendor's
   * questions), and a component branching three ways on state it does not own is a second place for
   * the rule to drift. The hook decides; this renders.
   */
  onContinue: VoidFunction;
  /* Which screen's CTA this is — the questions screen commits, so it reads Submit. */
  step: BookingAddOnsStep;
  /* A line has no price yet, so submitting commits to a quote rather than to a figure. */
  hasUnpriceable?: boolean;
  /*
   * Whether THIS press places the order. True on the questions screen, and on the picker when the
   * vendor asks nothing - the commoner path, since most bookings carry no vendor form.
   */
  commits?: boolean;
  /*
   * ⛔ The gate is ALSO enforced in `onSubmit`, and that redundancy is deliberate. This page has
   * already been bitten by a shared-library control whose `disabled` prop only greyed the icon while
   * the click handler stayed bound. Neither half may depend on the other being right.
   */
  isDisabled?: boolean;
}

interface CtaMessageInput {
  hasSelection: boolean;
  estimateTotal: number;
  estimateCurrency: string | null;
  locale: string;
  step: BookingAddOnsStep;
  hasUnpriceable?: boolean;
  commits?: boolean;
}

/*
 * The CTA's labels, as a pure function so the rules can be tested without rendering anything.
 *
 * The amount appears only on a basket that mixes priced and quote-required lines in one currency. A room
 * can offer several parties' products and currency arrives per item, so a figure summed across currencies
 * would be meaningless; it is withheld rather than invented, and the rows still show their own prices.
 */
export const ctaMessage = ({
  hasSelection,
  estimateTotal,
  estimateCurrency,
  locale,
  step,
  hasUnpriceable = false,
  commits = false,
}: CtaMessageInput): [{ id: string }, Record<string, string>?] => {
  /*
   * RES-1357. Wherever this button places the order, a selection carrying an unpriced line asks for
   * a quote and states no figure. Above the step branch because the picker commits too when the
   * vendor asks nothing, and a figure on the button that commits is what this exists to prevent.
   */
  if (hasUnpriceable && commits) {
    return [{ id: 'add_on_quote_submit' }];
  }

  /* The questions screen commits the order, so its verb is Submit rather than Continue. It carries no
   * figure: a quote basket is already answered above, so everything reaching here is fully priced. */
  if (step === 'questions') {
    return [{ id: 'add_on_submit' }];
  }

  if (!hasSelection) {
    return [{ id: 'add_on_continue_without' }];
  }

  /* A fully priced basket states its money in the summary card, not on the button. RES-1430 took the
   * figure off deliberately; if you put one back beside a commit verb it needs the per-locale estimate
   * hedge, see RES-1326. */
  if (!estimateCurrency || !hasUnpriceable) {
    return [{ id: 'add_on_continue' }];
  }

  return [
    { id: 'add_on_continue_with_estimate' },
    { total: formatCurrency(estimateTotal, estimateCurrency, locale, true, false) },
  ];
};

/*
 * The step's single action, which carries three states and its own copy rules — enough of its own job to
 * be its own component rather than a branch inside the page.
 *
 * ⭐ ONE BUTTON, RELABELLED — the design's shape and the shipped add-on page's too. With nothing selected
 * it IS the Skip: it makes no request of any kind and leaves the completed booking untouched. That
 * distinction is acceptance criterion 4, and on the shipped in-checkout page Skip and Complete dispatch
 * the identical handler; here they must not.
 */
export const StepCta = ({
  hasSelection,
  estimateTotal,
  estimateCurrency,
  isSubmitting,
  hasSubmitFailed,
  onContinue,
  step,
  hasUnpriceable = false,
  commits = false,
  isDisabled = false,
}: StepCtaProps): JSX.Element => {
  const intl = useIntl();
  const locale = useLocale();
  const label = intl.formatMessage(
    ...ctaMessage({ hasSelection, estimateTotal, estimateCurrency, locale, step, hasUnpriceable, commits }),
  );
  return (
    <CtaBar>
      {/*
        The server rejects a selection spanning more than one party with a 409, and refuses a submission
        for a building the feature is not on for. Neither is unreachable in normal use — the client-side
        grey-out is a convenience and explicitly not the enforcement — so the tenant is told rather than
        left looking at a button that did nothing.
      */}
      {hasUnpriceable && commits && (
        <CtaNote data-testid="booking-add-ons-quote-note">
          {intl.formatMessage({ id: 'add_on_quote_no_charge' })}
        </CtaNote>
      )}
      {hasSubmitFailed && (
        <SubmitError data-testid="booking-add-ons-error">
          {intl.formatMessage({ id: 'add_on_submit_failed' })}
        </SubmitError>
      )}
      <Cta
        type="button"
        data-testid="booking-add-ons-cta"
        onClick={onContinue}
        disabled={isSubmitting || isDisabled}
        aria-busy={isSubmitting}
      >
        {isSubmitting && (
          <CtaSpinner aria-hidden="true" data-testid="booking-add-ons-cta-spinner">
            <Spinner size="18px" color="currentColor" />
          </CtaSpinner>
        )}
        {label}
      </Cta>
    </CtaBar>
  );
};
